import { usePathname } from 'next/navigation';
import { omit } from 'ramda';
import { useEffect } from 'react';
import { useCookie, useSessionStorage } from 'react-use';

import config from '@/config';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { FORM_SUBMIT_TIMEOUT_MS } from '@/lib/utils/form-submit-timeout';

type FormSubmission = {
  data: { courseId?: string } & Record<string, unknown>;
  experimentId?: string;
  formType: string;
  formUrl?: string;
  variantId?: string;
};

export const useFormRequest = (): ((
  form: FormSubmission
) => Promise<Response>) => {
  const {
    pageData: { experimentId, variantId },
  } = usePageContext();
  const referrer = usePathname();

  const [cohortIdSpoof] = useSessionStorage<string | undefined>(
    'cohortIdSpoof'
  );

  // HACK: Special characters in cookies break requests to Vercel on Safari.
  // `gaconnector_region` can contain special characters and is set in GTM.
  // `ss_region` seems to be okay so use it's value if they differ.
  const [ssRegion] = useCookie('ss_region');
  const [gaRegion, setGaRegion] = useCookie('gaconnector_region');
  useEffect(() => {
    if (ssRegion && ssRegion !== gaRegion) {
      const { hostname } = window.location;
      // Make sure we use the same domain as Google Analytics
      const domain = hostname.replace('www.ironhack', '.ironhack');
      setGaRegion(ssRegion, { domain });
    }
  }, [gaRegion, setGaRegion, ssRegion]);

  return async (form: FormSubmission): Promise<Response> => {
    const { data, formUrl = '/new-api/forms', ...formData } = form;

    // Read the URL here instead of with `useSearchParams` so the hook doesn't
    // opt every form's page out of prerendering.
    // See https://nextjs.org/docs/app/api-reference/functions/use-search-params#behavior
    const referralCode =
      new URLSearchParams(window.location.search).get('ref_code') ?? '';

    let { courseId } = data;
    if (courseId && cohortIdSpoof && config.env !== 'production') {
      courseId = cohortIdSpoof;
    }

    const extendedForm = {
      data: {
        ...omit(['courseId'], data),
        ...(courseId && { courseId }),
      },
      experimentId,
      referralCode,
      referrer,
      variantId,
      ...formData,
    };

    const response = await fetch(formUrl, {
      body: JSON.stringify(extendedForm),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      signal: AbortSignal.timeout(FORM_SUBMIT_TIMEOUT_MS),
    });
    if (!response.ok) {
      throw new Error('Error sending form data to Salesforce');
    }
    return response;
  };
};
